// BottomNav — fixed-bottom tab bar, mobile only.
//
// Mounted by DashboardLayout when bottomNavItems is provided. Hidden on md+
// breakpoints (sidebar takes over there). Safe-area aware so the home
// indicator on iOS doesn't crush the icons.
//
// Active-state heuristic: `pathname === href || pathname.startsWith(href + '/')`,
// with a special-case for the role root (e.g. '/cont') so deeper paths like
// '/cont/comenzi/123' don't keep the dashboard tab highlighted.
//
// Tap behaviour:
//   - Single tap → navigate
//   - Double tap on the active tab → emit a `bottomnav:scroll-to-top` event
//     that the active page can listen to (or the layout can scroll the main
//     pane). Cheap to wire later; for now just navigate.

import { useCallback, useRef } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import type { BottomNavItem } from '@/lib/navigation'

interface Props {
  items: BottomNavItem[]
}

const DOUBLE_TAP_MS = 350

export default function BottomNav({ items }: Props) {
  const { pathname } = useLocation()
  const lastTapRef = useRef<{ to: string; at: number }>({ to: '', at: 0 })

  const isItemActive = useCallback(
    (to: string) => {
      // Role root (e.g. '/cont', '/firma') matches only on exact equality so
      // deeper paths don't keep the dashboard tab highlighted.
      const roleRoots = ['/cont', '/firma', '/worker', '/admin']
      if (roleRoots.includes(to)) return pathname === to
      return pathname === to || pathname.startsWith(to + '/')
    },
    [pathname],
  )

  const handleTap = useCallback((to: string) => {
    const now = Date.now()
    if (lastTapRef.current.to === to && now - lastTapRef.current.at < DOUBLE_TAP_MS) {
      window.dispatchEvent(new CustomEvent('bottomnav:scroll-to-top', { detail: { to } }))
    }
    lastTapRef.current = { to, at: now }
  }, [])

  return (
    <nav
      role="navigation"
      aria-label="Bottom navigation"
      className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white/95 dark:bg-gray-900/95 backdrop-blur border-t border-gray-200 dark:border-gray-800 safe-bottom"
    >
      <ul
        className="grid"
        style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}
      >
        {items.map((item) => {
          const Icon = item.icon
          const active = isItemActive(item.to)
          return (
            <li key={item.to} className="contents">
              <NavLink
                to={item.to}
                onClick={() => handleTap(item.to)}
                aria-current={active ? 'page' : undefined}
                className="flex flex-col items-center justify-center gap-0.5 py-2 px-1 active:opacity-60 transition-opacity min-h-[56px]"
              >
                <Icon
                  size={22}
                  strokeWidth={1.75}
                  className={active ? 'text-primary' : 'text-gray-500 dark:text-gray-400'}
                  aria-hidden="true"
                />
                <span
                  className={
                    'text-[10px] font-medium leading-tight ' +
                    (active ? 'text-primary' : 'text-gray-500 dark:text-gray-400')
                  }
                >
                  {item.label}
                </span>
              </NavLink>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
